import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { soloApi } from "@/lib/api";
import { AGENTS } from "@/lib/agents";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const STAGES = ["new", "contacted", "engaged", "won", "lost"];
const STAGE_COLOR = {
  new: "border-white/20", contacted: "border-neon-cyan/60",
  engaged: "border-neon-tangerine/60", won: "border-neon-lime/60", lost: "border-white/10 opacity-60",
};

export default function Sales() {
  const a = AGENTS.sales;
  const [leads, setLeads] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", company: "", notes: "" });
  const [drafting, setDrafting] = useState(null);
  const [draftText, setDraftText] = useState("");

  const load = () => soloApi.leads().then(({ data }) => setLeads(data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name || !form.email) return;
    await soloApi.createLead(form);
    setForm({ name: "", email: "", company: "", notes: "" });
    toast.success("Lead added");
    load();
  };

  const move = async (id, stage) => {
    await soloApi.moveLead(id, stage);
    load();
  };

  const draft = async (lead) => {
    setDrafting(lead);
    setDraftText("Rex is drafting…");
    const { data } = await soloApi.draftOutreach(lead.lead_id);
    setDraftText(data.email_text);
    toast.success("Draft ready — head to Approval Inbox to send it");
  };

  return (
    <div className="p-8 pb-16">
      <div className="flex items-center gap-4 mb-8">
        <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 3, repeat: Infinity }} className="w-16 h-16 slime-blob overflow-hidden" style={{ background: a.gradient }}>
          <img src={a.avatar} alt="Rex" className="w-full h-full object-cover mix-blend-luminosity" />
        </motion.div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: a.color }}>{a.role}</div>
          <h1 className="font-display text-4xl font-bold" style={{ color: a.color }}>Rex's pipeline</h1>
          <p className="text-white/70">Add a lead. I'll draft outreach. You approve before it goes out.</p>
        </div>

        <Dialog>
          <DialogTrigger asChild>
            <Button data-testid="sales-add-lead-btn" className="ml-auto rounded-full bg-neon-cyan text-slate-900 font-bold hover:bg-neon-cyan/90 neon-glow-cyan">
              <Plus className="w-4 h-4 mr-1" /> Add lead
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-white/20 text-white">
            <DialogHeader><DialogTitle className="font-display text-2xl">New lead</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input data-testid="sales-lead-name" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-white/20" />
              <Input data-testid="sales-lead-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white/5 border-white/20" />
              <Input data-testid="sales-lead-company" placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-white/5 border-white/20" />
              <Textarea data-testid="sales-lead-notes" placeholder="Notes (context for Rex)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-white/5 border-white/20" />
              <Button data-testid="sales-lead-save" onClick={add} className="w-full rounded-full bg-neon-cyan text-slate-900 font-bold neon-glow-cyan">Save lead</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {leads.length === 0 ? (
        <div className="glass rounded-3xl p-16 text-center">
          <div className="font-display text-2xl font-bold mb-2">No leads yet.</div>
          <p className="text-white/60">Add your first lead and Rex will draft outreach.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {leads.map((l) => (
            <motion.div
              key={l.lead_id}
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              whileHover={{ y: -4 }}
              className={"glass rounded-2xl p-5 border-2 " + STAGE_COLOR[l.stage]}
              data-testid={`sales-lead-${l.lead_id}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="font-display text-lg font-bold">{l.name}</div>
                  <div className="text-sm text-white/60">{l.company || l.email}</div>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-widest text-neon-cyan">{l.stage}</span>
              </div>
              {l.notes && <p className="text-sm text-white/70 mb-3">{l.notes}</p>}
              <div className="flex flex-wrap gap-1 mb-3">
                {STAGES.map((s) => (
                  <button
                    key={s}
                    data-testid={`sales-stage-${l.lead_id}-${s}`}
                    onClick={() => move(l.lead_id, s)}
                    className={"text-[10px] font-mono uppercase px-2 py-1 rounded-full " + (l.stage === s ? "bg-neon-cyan text-slate-900 font-bold" : "bg-white/5 text-white/60 hover:bg-white/10")}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <Button data-testid={`sales-draft-${l.lead_id}`} onClick={() => draft(l)} className="w-full rounded-full bg-white/10 hover:bg-white/20 text-white font-bold border border-white/20">
                <Mail className="w-4 h-4 mr-1" /> Draft outreach
              </Button>
            </motion.div>
          ))}
        </div>
      )}

      <Dialog open={!!drafting} onOpenChange={(o) => !o && setDrafting(null)}>
        <DialogContent className="bg-slate-900 border-white/20 text-white max-w-xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">Draft for {drafting?.name}</DialogTitle></DialogHeader>
          <div className="bg-slate-950/60 border border-white/10 rounded-2xl p-4 whitespace-pre-wrap font-mono text-sm text-neon-lime/90 max-h-96 overflow-y-auto">
            {draftText}
          </div>
          <p className="text-xs text-white/60 flex items-center gap-1"><ArrowRight className="w-3 h-3" /> Queued in Approval Inbox for your review.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
